import React, { FC, useState, useEffect } from 'react';

import { isBrowser } from '@lucid/utils';
import {
  useWindowWidth,
} from '@react-hook/window-size';
import { LucidTheme, mqMin } from '@lucid/theme';

import Box from '../box/box';
import CarouselDesktop from './carousel-desktop';
import CarouselMobile from './carousel-mobile';
import Heading from '../heading/heading';
import Text from '../text/text';
import { ComponentAnimation, ComponentImage, ComponentLink } from '../../types/generics';

export interface CarouselItems {
  /** The animation data for the `Carousel`. */
  animation?: ComponentAnimation;
  /** The text to be displayed in the bottom of the `Carousel`. */
  footerText?: string;
  /** The item heading. */
  heading?: string;
  /** The item image. */
  image?: ComponentImage;
  /** The item subheading. */
  subheading?: string;
}

export interface BaseCarouselProps {
  /** The `Carousel` cta. */
  cta?: ComponentLink;
  /** The `Carousel` items. */
  items: CarouselItems[];
}

export interface BaseCarouselControlsProps {
  /** Changes the position of the controls for the `Carousel`. */
  controlsPosition?: 'left' | 'right' | 'bottom';
}

export interface BaseCarouselMobileImageProps {
  /** Checks if the images should overflow the container on mobile. */
  shouldOverflowMobileImages?: boolean;
}

export interface CarouselProps extends BaseCarouselProps,
  BaseCarouselControlsProps,
  BaseCarouselMobileImageProps {
  /** Sets the `Carousel` container to have a grid column. */
  hasGrid?: boolean;
  /** The heading for the `Carousel` section. */
  heading?: string;
  /** The subheading for the `Carousel` section. */
  subheading?: string;
}

const Carousel: FC<CarouselProps> = (
  {
    controlsPosition = 'left',
    cta,
    items,
    hasGrid = false,
    heading,
    shouldOverflowMobileImages,
    subheading
  }
) => {
  const width = useWindowWidth();
  const [isLoading, setIsLoading] = useState(true);
  const hasHeading = !!heading;
  const hasSubheading = !!subheading;
  // Workaround for getting carousel to load properly
  useEffect(() => {
    setIsLoading(false);
  }, []);

  if (!items.length) return null;

  if (!isBrowser || isLoading) {
    return <Box css={{ height: 0, paddingBottom: '50%' }} />;
  }

  return (
    <Box
      as="section"
      css={(theme: LucidTheme) => ({
        label: 'carousel',
        paddingBottom: theme.space[7],
        paddingTop: theme.space[7],
        textAlign: 'center'
      })}
    >
      {hasHeading && (
        <Heading
          as="h2"
          css={(theme: LucidTheme) => ({
            margin: 'auto',
            marginBottom: theme.space[3],
            maxWidth: '100%',
            textAlign: 'center',
            [mqMin.tabletSmall]: {
              maxWidth: '20ch'
            }
          })}
          variant="headline2XL"
        >
          {heading}
        </Heading>
      )}
      {hasSubheading && (
        <Text
          css={(theme: LucidTheme) => (
            {
              maxWidth: '50ch',
              margin: `${theme.space[0]} auto ${theme.space[4]}px`
            }
          )}
          variant="paragraphM400"
        >
          {subheading}
        </Text>
      )}
      <Box
        as="section"
        css={(theme: LucidTheme) => [
          {
            label: 'carouselSection',
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            paddingTop: theme.space[9],
            paddingBottom: theme.space[7]
          },
          hasHeading && {
            paddingTop: theme.space[3]
          },
          controlsPosition === 'right' && {
            alignItems: 'initial'
          }
        ]}
        gridColumn={hasGrid ? '1 / -1' : undefined}
      >
        {
          width >= 768 ? (
            <CarouselDesktop controlsPosition={controlsPosition} cta={cta} items={items} />
          ) : (
            <CarouselMobile cta={cta} items={items} shouldOverflowMobileImages={shouldOverflowMobileImages} />
          )
        }
      </Box>
    </Box>
  );
};

export { Carousel };
export default Carousel;
